// BestSeller.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { connect } from 'react-redux';
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import BestSellerCard from "./BestSellerCard";
import LoadingSkeleton from "./LoadingSkeleton";
import "./index.css";

function BestSeller({ t, vendor_name }) {
  const [bestSellerProduct, setBestSellerProduct] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBestSellers = useCallback(async () => {
    // if (!vendor_name) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/best-seller?vendor_name=${encodeURIComponent(vendor_name)}`,
        { signal: controller.signal }
      );
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setBestSellerProduct(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
        console.error('Failed to fetch best sellers:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [vendor_name]);

  useEffect(() => {
    fetchBestSellers();
  }, [fetchBestSellers]);

  const sliderSettings = useMemo(() => ({
    dots: true,
    infinite: bestSellerProduct.length > 4,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: bestSellerProduct.length > 4,
    speed: 500,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    pauseOnFocus: true,
    lazyLoad: 'ondemand',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          dots: false,
          arrows: false
        }
      }
    ]
  }), [bestSellerProduct.length]);

  const displayProducts = useMemo(() => 
    bestSellerProduct.slice(0, 12), 
    [bestSellerProduct]
  );

  if (loading) {
    return (
      <div className="container p-4">
        <h2 className="section-title">{t("bseller")}</h2>
        <LoadingSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container p-4">
        <h2 className="section-title">{t("bseller")}</h2>
        <div className="error-message">
          <p>Failed to load best sellers. Please try again.</p>
          <button onClick={fetchBestSellers} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!displayProducts.length) {
    return (
      <div className="container p-4">
        <h2 className="section-title">{t("bseller")}</h2>
        <div className="empty-state">
          <p>No best sellers available at the moment.</p>
        </div>
      </div>
    );
  }

  return (
    <section className="best-seller-section">
      <div className="container p-4">
        <div className="section-header">
          <h2 className="section-title">{t("bseller")}</h2>
          <div className="section-subtitle">Discover our most popular products</div>
        </div>
        
        <div className="slick-container">
          <Slider {...sliderSettings}>
            {displayProducts.map((product, index) => (
              <div key={`${product.id}-${index}`} className="slick-item-container">
                <BestSellerCard
                  productObj={product}
                  t={t}
                />
              </div>
            ))}
          </Slider>
        </div>
      </div>
    </section>
  );
}

const mapStateToProps = (state) => ({
  vendor_name: state.globalState.selectedProductVendorName,
});

export default connect(mapStateToProps)(BestSeller);